import { useState } from 'react';
import { DataContainer } from "containers/DataContainer";
import { ISentiment, ISuggestionCatchUp, IWorkExperience, IJob, IProject, IGoal } from "types";
import { sendEmail, saveEmbedding, getCompletion, pickBestMatchforItem, pickBestGroupforItem } from "utils";

// Declare module interfaces
export interface IGoalSummary {
    title: string;
    description: string;
}

export interface IExperienceDescription {
    experience: string;
}

// Declare return type for the hook
export interface ICatchUpProcessing {
    submittingForm: boolean;
    setSubmittingForm: (value: boolean) => void;
    getGeneratedWorkExperiences: (description: string) => Promise<string>;
    getGeneratedSuggestions: (description: string) => Promise<string>;
    getGeneratedSummary: (description: string) => Promise<string>;
    getGeneratedSentiment: (description: string) => Promise<string>;
}

export const useCatchUpProcessing = (): ICatchUpProcessing => {
    const {
        EmptyWorkExperience,
        EmptySentiment,
        goals,
        jobs,
        workExperiences,
        projects,
        sentiments,
        setSentiments,
        setWorkExperiences,
        preferredName,
        user,
        userEmail,
    } = DataContainer.useContainer();

    const [submittingForm, setSubmittingForm] = useState<boolean>(false);

    // Move all the shared functions here
    const pickSuggestedJob = async (itemId: string): Promise<string> => {
        const currentJobs = jobs.filter((job: IJob) => !job.endDate && workExperiences.some((experience: IWorkExperience) => experience.jobId === job.itemId));

        if (currentJobs.length) {
            if (currentJobs.length > 1) {
                const jobExperienceGroups: string[][] = [];
                // TODO: Make sure all jobs in the currentJobs array have work experiences associated with them. If not, skip them. If at the end there is only one job left,  then do not pickBestGroupForItem.
                currentJobs.map((job: IJob) => {
                    const jobExperienceGroup: string[] = [];
                    workExperiences
                        .filter((experience: IWorkExperience) => experience.jobId === job.itemId)
                        .map((experience: IWorkExperience) => {
                            jobExperienceGroup.push(experience.itemId);
                        });
                    jobExperienceGroups.push(jobExperienceGroup);
                });
                const bestGroupIndex = await pickBestGroupforItem(itemId, jobExperienceGroups);
                return currentJobs[bestGroupIndex].itemId;
            } else {
                return currentJobs[0].itemId;
            }
        } else {
            return "";
        }
    };

    const pickSuggestedProject = async (itemId: string, jobId: string): Promise<string> => {
        // Decide on suggested project

        const projectSuggestionOptions: IProject[] = projects.filter(
            (project: IProject) => project.jobId === jobId && workExperiences.some((experience: IWorkExperience) => experience.projectId === project.itemId),
        );

        if (projectSuggestionOptions.length) {
            const projectSuggestionOptionIds: string[] = [];

            const projectEmpty: string[] = [];

            const projectExperienceGroups: string[][] = [];

            projectSuggestionOptions.map(async (project: IProject) => {
                projectSuggestionOptionIds.push(project.itemId);
                const projectExperiences: string[] = [];
                const filterWorkedExperiences: IWorkExperience[] = workExperiences.filter(
                    (experience: IWorkExperience) => experience.projectId === project.itemId,
                );

                if (filterWorkedExperiences.length) {
                    filterWorkedExperiences.map((experience: IWorkExperience) => {
                        projectExperiences.push(experience.itemId);
                    });
                } else {
                    projectEmpty.push(project.itemId);
                }
                projectExperienceGroups.push(projectExperiences);
            });

            const unfilteredWorkExperiences = workExperiences.filter(
                (experience: IWorkExperience) => experience.jobId === jobId && experience.projectId === "",
            );

            if (unfilteredWorkExperiences.length) {

                const unfilteredWorkExperienceIds: string[] = [];
                unfilteredWorkExperiences.map((experience: IWorkExperience) => {
                    unfilteredWorkExperienceIds.push(experience.itemId);
                });

                projectExperienceGroups.push(unfilteredWorkExperienceIds);
            }

            const bestGroupIndex = await pickBestGroupforItem(itemId, projectExperienceGroups);

            if (bestGroupIndex === projectExperienceGroups.length || bestGroupIndex === -1 || projectEmpty.includes(projectSuggestionOptions[bestGroupIndex].itemId)) {
                return "";
            } else {
                return projectSuggestionOptions[bestGroupIndex].itemId;
            }
        } else {
            return "";
        }
    };

    const pickSuggestedGoal = async (itemId: string): Promise<string> => {
        const goalIds: string[] = goals.map((goal: IGoal) => goal.itemId);

        const bestGoal = await pickBestMatchforItem(itemId, goalIds);

        return bestGoal;
    };

    const getGeneratedWorkExperiences = async (description: string): Promise<string> => {
        try {
            const experienceResponse = await getCompletion(description, "catchUpExperienceGeneration");

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const experienceResponsePrepped = JSON.parse(experienceResponse);
            const experienceDescriptions = experienceResponsePrepped.experiences;
            if (experienceDescriptions) {
                const processExperiences = async () => {
                    const newWorkExperiences: IWorkExperience[] = await Promise.all(
                        experienceDescriptions.map(async (experience: IExperienceDescription) => {
                            const newWorkExperience: IWorkExperience = new EmptyWorkExperience(user);
                            newWorkExperience.description = experience.experience;

                            try {
                                const response = await saveEmbedding(newWorkExperience.itemId, newWorkExperience.description);
                                if (response !== "Error") {
                                    newWorkExperience.jobId = await pickSuggestedJob(newWorkExperience.itemId);

                                    if (newWorkExperience.jobId !== "") {
                                        // Set suggested project
                                        newWorkExperience.projectId = await pickSuggestedProject(
                                            newWorkExperience.itemId,
                                            newWorkExperience.jobId,
                                        );
                                    }
                                    // Set suggested goal
                                    newWorkExperience.goalId = await pickSuggestedGoal(newWorkExperience.itemId);
                                } else {
                                    console.error("Failed to save embedding");
                                }
                            } catch (error) {
                                console.error("Error saving embedding:", error);
                            }

                            const today = new Date();
                            newWorkExperience.dateCreated = today.toISOString();
                            newWorkExperience.completionDate = today.toISOString();

                            return newWorkExperience;
                        }),
                    );

                    const updatedWorkExperiences = [...newWorkExperiences, ...workExperiences];
                    setWorkExperiences(updatedWorkExperiences);

                    const currentRelatedExperience: string[] = newWorkExperiences.map(
                        (experience: IWorkExperience) => experience.itemId,
                    );
                    return JSON.stringify(currentRelatedExperience);
                };
                const experiencesFromCatchup: string = await processExperiences();
                return experiencesFromCatchup;
            } else {
                return "";
            }
        } catch (error: unknown) {
            try {
                sendEmail("Recommendation Error", error, preferredName, "supportRequest", userEmail);
                if (
                    process.env.REACT_APP_USER_BRANCH !== "master" ||
                    (process.env.REACT_APP_USER_BRANCH === "master" &&
                        userEmail === "releasetest@careerminder.io")
                ) {
                    console.log("Error", error);
                    return "Error, sent a report";
                } else {
                    return "Error, couldn’t send a report";
                }
            } catch (err: unknown) {
                if (
                    process.env.REACT_APP_USER_BRANCH !== "master" ||
                    (process.env.REACT_APP_USER_BRANCH === "master" &&
                        userEmail === "releasetest@careerminder.io")
                ) {
                    console.log("Error sending error report", err);
                }
                return "Error";
            }
        }
    };

    const getGeneratedSuggestions = async (description: string): Promise<string> => {
        try {
            const goalsToSend: IGoalSummary[] = [];

            goals.map((goal: IGoal) => {
                const goalToSend: IGoalSummary = {
                    title: goal.goal,
                    description: goal.details,
                };
                goalsToSend.push(goalToSend);
            });

            const payload = {
                goals: goalsToSend,
                recentWorkDescription: description,
            };

            const suggestionResponse = await getCompletion(
                JSON.stringify(payload),
                "catchUpSuggestionsGeneration",
            );

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const suggestionResponsePrepped = JSON.parse(suggestionResponse);
            const suggestions = suggestionResponsePrepped.suggestions;
            if (suggestions) {
                const suggestionsForCatchUp: ISuggestionCatchUp[] = [];

                suggestions.map((suggestion: ISuggestionCatchUp) => {
                    suggestionsForCatchUp.push(suggestion);
                });

                return JSON.stringify(suggestionsForCatchUp);
            } else {
                return "";
            }
        } catch (error: unknown) {
            try {
                sendEmail("Recommendation Error", error, preferredName, "supportRequest", userEmail);
                if (
                    process.env.REACT_APP_USER_BRANCH !== "master" ||
                    (process.env.REACT_APP_USER_BRANCH === "master" &&
                        userEmail === "releasetest@careerminder.io")
                ) {
                    console.log("Error", error);
                    return "Error, sent a report";
                } else {
                    return "Error, couldn’t send a report";
                }
            } catch (err: unknown) {
                if (
                    process.env.REACT_APP_USER_BRANCH !== "master" ||
                    (process.env.REACT_APP_USER_BRANCH === "master" &&
                        userEmail === "releasetest@careerminder.io")
                ) {
                    console.log("Error sending error report", err);
                }
                return "Error";
            }
        }
    };

    const getGeneratedSummary = async (description: string): Promise<string> => {
        try {
            const responseSummary = await getCompletion(description, "catchUpSummaryGeneration");
            return JSON.parse(responseSummary);
        } catch (error: unknown) {
            try {
                sendEmail("Recommendation Error", error, preferredName, "supportRequest", userEmail);
                if (
                    process.env.REACT_APP_USER_BRANCH !== "master" ||
                    (process.env.REACT_APP_USER_BRANCH === "master" &&
                        userEmail === "releasetest@careerminder.io")
                ) {
                    console.log("Error", error);
                    return "Error, sent a report";
                } else {
                    return "Error, couldn’t send a report";
                }
            } catch (err: unknown) {
                if (
                    process.env.REACT_APP_USER_BRANCH !== "master" ||
                    (process.env.REACT_APP_USER_BRANCH === "master" &&
                        userEmail === "releasetest@careerminder.io")
                ) {
                    console.log("Error sending error report", err);
                }
                return "Error";
            }
        }
    };

    const getGeneratedSentiment = async (description: string): Promise<string> => {
        try {
            // TODO: Look into using direct sentiment analysis. Maybe with Mistral 8x7b on Anyscale. This could be a good option for replacing OpenAI entirely. The cost could certainly be lower

            const responseContent = await getCompletion(description, "catchUpSentimentGeneration");
            const responseContentParsed = JSON.parse(responseContent);
            const newSentiment: ISentiment = new EmptySentiment(user);
            if (responseContentParsed?.toLowerCase().includes("positive")) {
                newSentiment.feeling = 100; // Positive sentiment
            } else if (responseContentParsed?.toLowerCase().includes("negative")) {
                newSentiment.feeling = 0; // Negative sentiment
            } else {
                newSentiment.feeling = 50; // Neutral sentiment
            }
            setSentiments([...[newSentiment], ...sentiments]);
            return newSentiment.itemId;
        } catch (error: unknown) {
            try {
                sendEmail("Recommendation Error", error, preferredName, "supportRequest", userEmail);
                if (
                    process.env.REACT_APP_USER_BRANCH !== "master" ||
                    (process.env.REACT_APP_USER_BRANCH === "master" &&
                        userEmail === "releasetest@careerminder.io")
                ) {
                    console.log("Error", error);
                    return "Error, sent a report";
                } else {
                    return "Error, didn’t send a report";
                }
            } catch (err: unknown) {
                if (
                    process.env.REACT_APP_USER_BRANCH !== "master" ||
                    (process.env.REACT_APP_USER_BRANCH === "master" &&
                        userEmail === "releasetest@careerminder.io")
                ) {
                    console.log("Error sending error report", err);
                }
                return "Error";
            }
        }
    };

    return {
        submittingForm,
        setSubmittingForm,
        getGeneratedWorkExperiences,
        getGeneratedSuggestions,
        getGeneratedSummary,
        getGeneratedSentiment
    };
}; 